import { ReactNode } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useTranslation } from '@frontend/i18n';
import { AssociatedContactsListItem } from './associated-contacts-list-item';
import { ContactsDrawerHeader } from './atoms/contacts-drawer-header';
import { type ContactListItemVariant } from './contact-list-item';
import { contactsListStyles } from './contacts-drawer.styles';
import { ThreadPopoverDrawer, type ThreadPopoverDrawerProps } from './thread-popover-drawer';
import { UnknownContactListItem } from './unknown-contact-list-item';

export type DrawerContact = {
  personId?: string;
  person?: Person;
  phoneNumber?: string;
  isPrimary?: boolean;
  rightSlot?: ReactNode;
  nameAreaSlot?: ReactNode;
  /** When true, the contact row renders with subdued text styling */
  isOptedOut?: boolean;
  /** When true, the contact row renders with subdued text styling (blocked) */
  isBlocked?: boolean;
};

type ContactsDrawerBaseProps = Omit<ThreadPopoverDrawerProps, 'children' | 'closeLabel' | 'open' | 'onClose'> & {
  contacts: DrawerContact[];
  title: string;
  subtitle?: string;
  count?: number;
  compact?: boolean;
  open: boolean;
  onClose: () => void;
  onSelect: (personId: string) => void;
  onSetPrimaryContact?: (personId: string) => void;
  locationId: string;
  rightSlot?: ReactNode;
  variant?: ContactListItemVariant;
};

export const ContactsDrawerBase = ({
  contacts,
  title,
  subtitle,
  count,
  compact,
  open,
  onClose,
  onSelect,
  onSetPrimaryContact,
  locationId,
  rightSlot,
  variant,
  ...rest
}: ContactsDrawerBaseProps) => {
  const { t } = useTranslation('thread-header');

  return (
    <ThreadPopoverDrawer open={open} onClose={onClose} closeLabel={t('Close')} {...rest}>
      <ContactsDrawerHeader
        title={title}
        subtitle={subtitle}
        count={count ?? contacts.length}
        onClose={onClose}
        compact={compact}
      />
      <ul css={contactsListStyles}>
        {contacts.map((contact) => {
          if (contact.personId) {
            const personId = contact.personId;
            const isGroupVariant = variant === 'group';

            return (
              <AssociatedContactsListItem
                key={personId}
                personId={personId}
                person={contact.person}
                disableRowInteraction={isGroupVariant}
                onSelect={() => {
                  onSelect(personId);
                  onClose();
                }}
                onSetPrimaryContact={
                  onSetPrimaryContact
                    ? () => {
                        onSetPrimaryContact(personId);
                        onClose();
                      }
                    : undefined
                }
                isPrimary={contact.isPrimary}
                isOptedOut={contact.isOptedOut}
                isBlocked={contact.isBlocked}
                rightSlot={contact.rightSlot ?? rightSlot}
                nameAreaSlot={contact.nameAreaSlot}
                showSecondaryInfo={!compact && isGroupVariant}
                locationId={locationId}
                size='small'
                variant={variant}
              />
            );
          }

          if (contact.phoneNumber) {
            return (
              <UnknownContactListItem
                key={contact.phoneNumber}
                phoneNumber={contact.phoneNumber}
                isOptedOut={contact.isOptedOut}
                isBlocked={contact.isBlocked}
                nameAreaSlot={contact.nameAreaSlot}
                size='small'
                variant={variant}
                onSelect={() => {
                  onSelect(contact.phoneNumber as string);
                  onClose();
                }}
              />
            );
          }

          return null;
        })}
      </ul>
    </ThreadPopoverDrawer>
  );
};
